const API_BASE =
  import.meta.env['VITE_API_URL'] ?? (import.meta.env.DEV ? '' : 'https://api.fixmyseo.io');

let _token: string | null = null;
// Deduplicated in-flight refresh promise — prevents parallel 401s from triggering multiple refreshes
let _pendingRefresh: Promise<void> | null = null;

/** Thrown for any non-2xx response that isn't a SwapLimitError. Carries the parsed response body. */
export class ApiError extends Error {
  readonly status: number;
  readonly data: unknown;
  constructor(status: number, data: unknown) {
    const msg =
      typeof data === 'object' && data !== null && 'error' in data
        ? String((data as Record<string, unknown>)['error'])
        : `HTTP ${status}`;
    super(msg);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export class SwapLimitError extends Error {
  readonly next_reset_at: string;
  readonly current_tier: string;
  readonly swaps_used: number;
  readonly swaps_limit: number;
  readonly upgrade_url: string | null;
  constructor(data: {
    message: string;
    next_reset_at: string;
    current_tier: string;
    swaps_used: number;
    swaps_limit: number;
    upgrade_url: string | null;
  }) {
    super(data.message);
    this.name = 'SwapLimitError';
    this.next_reset_at = data.next_reset_at;
    this.current_tier = data.current_tier;
    this.swaps_used = data.swaps_used;
    this.swaps_limit = data.swaps_limit;
    this.upgrade_url = data.upgrade_url;
  }
}

export function setToken(token: string | null): void {
  _token = token;
}

export function getToken(): string | null {
  return _token;
}

async function doRefresh(): Promise<void> {
  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Refresh failed');
  const data = (await res.json()) as { accessToken: string };
  _token = data.accessToken;
  localStorage.setItem('fixmyseo_access_token', data.accessToken);
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  _isRetry = false,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (_token) headers['Authorization'] = `Bearer ${_token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  // Auto-refresh on 401 — but not for auth endpoints (avoid loops) and not on a retry
  if (res.status === 401 && !_isRetry && !path.startsWith('/auth/')) {
    try {
      if (!_pendingRefresh) {
        _pendingRefresh = doRefresh().finally(() => { _pendingRefresh = null; });
      }
      await _pendingRefresh;
      return request<T>(method, path, body, true);
    } catch {
      // Refresh failed — throw the original 401 so App.tsx can redirect to login
    }
  }

  const data: unknown = await res.json();
  if (!res.ok) {
    // Special handling for swap budget exhaustion
    if (
      res.status === 429 &&
      typeof data === 'object' && data !== null &&
      (data as Record<string, unknown>)['error'] === 'swap_limit_reached'
    ) {
      const d = data as Record<string, unknown>;
      throw new SwapLimitError({
        message:      String(d['message'] ?? 'Swap limit reached'),
        next_reset_at: String(d['next_reset_at'] ?? ''),
        current_tier:  String(d['current_tier'] ?? ''),
        swaps_used:    Number(d['swaps_used'] ?? 0),
        swaps_limit:   Number(d['swaps_limit'] ?? 0),
        upgrade_url:   typeof d['upgrade_url'] === 'string' ? d['upgrade_url'] : null,
      });
    }
    throw new ApiError(res.status, data);
  }
  return data as T;
}

export const api = {
  auth: {
    login: (email: string, password: string) =>
      request<{ accessToken: string; user: { id: string; email: string; tier: string } }>(
        'POST',
        '/auth/login',
        { email, password },
      ),
    signup: (email: string, password: string, tier?: string) =>
      request<{ accessToken: string; refreshTokenSet: boolean; stripeCheckoutUrl: string | null }>(
        'POST', '/auth/signup', { email, password, ...(tier ? { tier } : {}) },
      ),
    verifyEmail: (token: string) =>
      request<{ message: string }>('POST', '/auth/verify-email', { token }),
    resendVerification: (email: string) =>
      request<{ message: string }>('POST', '/auth/resend-verification', { email }),
    refresh: () => request<{ accessToken: string; tier: string; email: string }>('POST', '/auth/refresh'),
    logout: () => request<void>('POST', '/auth/logout'),
    forgotPassword: (email: string) =>
      request<{ message: string }>('POST', '/auth/forgot-password', { email }),
    resetPassword: (token: string, password: string) =>
      request<{ message: string }>('POST', '/auth/reset-password', { token, password }),
  },

  audits: {
    getFindings: (auditId: string) =>
      request<{ auditId: string; auditedAt: string; findings: import('./types').AuditFinding[] }>('GET', `/audits/${auditId}/findings`),
    rescoreOnRead: (auditId: string) =>
      request<import('./types').RescoreResponse>('POST', `/audits/${auditId}/rescore-on-read`),
  },

  domains: {
    list: () => request<import('./types').DomainSummary[]>('GET', '/domains'),
    get: (id: string) => request<import('./types').DomainDetail>('GET', `/domains/${id}`),
    create: (domain: string) =>
      request<import('./types').DomainDetail>('POST', '/domains', { domain }),
    detectPlatform: (url: string) =>
      request<{ isWordPress: boolean; confidence: 'high' | 'medium' | 'low' }>(
        'POST', '/domains/detect-platform', { url },
      ),
    setInstallPath: (id: string, path: 'self' | 'developer_email' | 'share_link') =>
      request<{ installPath: string }>('POST', `/domains/${id}/install/path`, { path }),
    sendDeveloperEmail: (
      id: string,
      developerEmail: string,
      developerName?: string,
      siteUrl?: string,
    ) =>
      request<{ sent: boolean; to: string }>(
        'POST',
        `/domains/${id}/install/email`,
        { developerEmail, developerName, siteUrl },
      ),
    getSnippet: (id: string) => request<{ snippet: string }>('GET', `/domains/${id}/snippet`),

    // Sprint 2.4
    getContent: (id: string) =>
      request<import('./types').ContentResponse>('GET', `/domains/${id}/content`),
    saveContent: (id: string, fieldPath: string, humanValue: string, aiValue?: string) =>
      request<{ edit: import('./types').ContentEditEntry }>(
        'POST',
        `/domains/${id}/content`,
        { field_path: fieldPath, human_value: humanValue, ai_value: aiValue },
      ),
    getBusinessContext: (id: string) =>
      request<import('./types').BusinessContext | null>('GET', `/domains/${id}/business-context`),
    patchBusinessContext: (
      id: string,
      data: {
        coreOffering: string;
        coreOfferingCategory: string;
        adjacentOfferings?: string[] | null;
        geographyCountry: string;
        geographyCity: string;
        businessModel?: string | null;
      },
    ) =>
      request<import('./types').BusinessContext>('PATCH', `/domains/${id}/business-context`, data),
    clearBusinessContextConfirm: (id: string) =>
      request<{ ok: boolean }>('DELETE', `/domains/${id}/business-context/confirm`),
    patchAudienceContext: (id: string, audience: 'b2c' | 'b2b' | 'both') =>
      request<import('./types').BusinessContext>('PATCH', `/domains/${id}/business-context/audience`, { audience }),
    getTargetKeywords: (id: string) =>
      request<import('./types').TargetKeywordsResponse>('GET', `/domains/${id}/target-keywords`),
    putTargetKeywords: (id: string, keywords: string[]) =>
      request<import('./types').TargetKeywordsResponse>('PUT', `/domains/${id}/target-keywords`, { keywords }),
    getCitationHistory: (id: string, params?: { since?: string; platform?: string; limit?: number }) => {
      const qs = new URLSearchParams();
      if (params?.since)    qs.set('since', params.since);
      if (params?.platform) qs.set('platform', params.platform);
      if (params?.limit !== undefined) qs.set('limit', String(params.limit));
      const query = qs.toString();
      return request<import('./types').CitationHistoryResponse>(
        'GET',
        `/domains/${id}/citation-history${query ? `?${query}` : ''}`,
      );
    },
    getSuggestions: (id: string) =>
      request<import('./types').Suggestion[]>('GET', `/domains/${id}/suggestions`),
    getAudits: (id: string) =>
      request<import('./types').DomainAuditHistory>('GET', `/domains/${id}/audits`),
    update: (id: string, domain: string) =>
      request<{ ok: boolean; domain: string }>('PATCH', `/domains/${id}`, { domain }),
    delete: (id: string) =>
      request<{ ok: boolean }>('DELETE', `/domains/${id}`),
    recover: (id: string) =>
      request<{ id: string; domain: string; verified: boolean; state: string; type: string; installSnippet: string; createdAt: string }>(
        'POST',
        `/domains/${id}/recover`,
      ),
    listDeleted: () =>
      request<Array<{ id: string; domain: string; deletedAt: string; createdAt: string }>>('GET', '/domains/deleted'),
    getSwapBudget: () =>
      request<{ swapsUsed: number; swapsLimit: number; swapsRemaining: number; billingPeriodStart: string; nextResetAt: string }>(
        'GET',
        '/domains/swaps',
      ),
    reaudit: (id: string) =>
      request<{ ok: boolean; message: string }>('POST', `/domains/${id}/audit`),
    refreshBrandVoice: (id: string) =>
      request<{ ok: boolean; jobId: string }>('POST', `/domains/${id}/refresh-brand-voice`),
    scanStatus: (id: string) =>
      request<{ scanning: boolean; pollToken?: string; isLongScan?: boolean; startedAt?: string }>(
        'GET',
        `/api/v1/domains/${id}/scan-status`,
      ),
    setPreferences: (id: string, prefs: { digestEmailsEnabled?: boolean; sweepOptedOutPlatforms?: string[] }) =>
      request<{ ok: boolean }>('PATCH', `/domains/${id}/preferences`, prefs),
    setAggregateConsent: (id: string, contributes: boolean) =>
      request<{ ok: boolean; domain: string; contributes: boolean; updatedAudits: number }>(
        'PATCH',
        `/domains/${id}/aggregate-consent`,
        { contributes },
      ),
    getInterrogationResults: (id: string) =>
      request<import('./types').InterrogationResultsResponse>('GET', `/domains/${id}/interrogation-results`),
    overview: () =>
      request<import('./types').DomainsOverviewResponse>('GET', '/domains/overview'),
    setClientLabel: (id: string, label: string | null) =>
      request<{ ok: boolean }>('PATCH', `/domains/${id}`, { client_label: label }),
  },

  verification: {
    initiate: (domain: string) =>
      request<{
        token: string;
        domain: string;
        expiresAt: string;
        fileUploadPath: string;
        dnsTxtHost: string;
        verificationMethods: Array<{ id: string; instructions: string; snippet: string; priority: number }>;
      }>('POST', '/domain/verify/initiate', { domain }),

    confirm: (token: string, method: string) =>
      request<{
        verified: boolean;
        methodUsed: string;
        verifiedAt: string;
        domain: string;
        unlockedFeatures: string[];
      }>('POST', '/domain/verify/confirm', { token, method }),

    status: (domain: string) =>
      request<{
        domain: string;
        verifiedByMe: boolean;
        myVerification: {
          verificationId: string;
          methodUsed: string;
          verifiedAt: string;
          lastRecheckedAt: string | null;
          recheckFailures: number;
        } | null;
        coManagerCount: number;
      }>('GET', `/domain/verify/status?domain=${encodeURIComponent(domain)}`),

    revoke: (domain: string) =>
      request<{ ok: boolean; domain: string }>('POST', '/domain/verify/revoke', { domain }),

    takedown: (domain: string, reason: string, attestation: string) =>
      request<{ ok: boolean; domain: string; takedownRequested: boolean; scheduledDeleteAt: string }>(
        'POST',
        '/domain/takedown',
        { domain, reason, attestation },
      ),

    reverseTakedown: (domain: string) =>
      request<{ ok: boolean; domain: string; takedownRequested: boolean }>(
        'POST',
        '/domain/takedown',
        { domain, reason: '', attestation: '', reverse: true },
      ),
  },

  suggestions: {
    updateStatus: (
      id: string,
      status: import('./types').SuggestionStatus,
      dismissalReason?: string,
    ) =>
      request<{ ok: boolean; triggerKind?: string }>(
        'PATCH',
        `/suggestions/${id}/status`,
        { status, dismissal_reason: dismissalReason },
      ),
    markPreExisting: (id: string) =>
      request<{ ok: boolean }>('POST', `/suggestions/${id}/mark-pre-existing`),
  },

  billing: {
    checkout: (tier: 'tier_1' | 'tier_2' | 'tier_3') =>
      request<{ url: string }>('POST', '/billing/checkout', { tier }),
    portal: () => request<{ url: string }>('POST', '/billing/portal'),
    resolveSession: (sessionId: string) =>
      request<{ tier: string }>('GET', `/billing/session/${sessionId}`),
  },

  competitors: {
    list: (ownedDomainId: string) =>
      request<import('./types').CompetitorSuggestion[]>('GET', `/competitors/${ownedDomainId}`),
    updateStatus: (
      id: string,
      ownedDomainId: string,
      status: 'accepted' | 'rejected',
    ) =>
      request<{ ok: boolean }>('PATCH', `/competitors/${id}/status`, { status, ownedDomainId }),
    reorder: (ownedDomainId: string, ids: string[]) =>
      request<{ ok: boolean }>('PATCH', `/competitors/${ownedDomainId}/reorder`, { orderedIds: ids }),
    addManual: (ownedDomainId: string, hostname: string) =>
      request<import('./types').CompetitorSuggestion>(
        'POST',
        `/competitors/${ownedDomainId}/manual`,
        { hostname },
      ),
    remove: (id: string) => request<{ ok: boolean }>('DELETE', `/competitors/${id}`),
    refreshCompetitors: (domainId: string) =>
      request<{ ok: true; jobId: string } | { status: 'already_queued' }>('POST', `/domains/${domainId}/refresh-competitors`),
  },

  locations: {
    list: (domainId: string) =>
      request<import('./types').DomainLocation[]>('GET', `/locations/${domainId}`),
    add: (
      domainId: string,
      data: { locationCode: string; countryCode: string; city?: string; isPrimary?: boolean },
    ) => request<import('./types').DomainLocation>('POST', `/locations/${domainId}`, data),
    remove: (domainId: string, locationId: string) =>
      request<{ ok: boolean }>('DELETE', `/locations/${domainId}/${locationId}`),
    setPrimary: (domainId: string, locationId: string) =>
      request<{ ok: boolean }>('PATCH', `/locations/${domainId}/${locationId}/primary`),
    update: (
      domainId: string,
      locationId: string,
      fields: { city?: string; countryCode?: string },
    ) =>
      request<import('./types').DomainLocation>('PATCH', `/locations/${domainId}/${locationId}`, fields),
  },

  reach: {
    set: (domainId: string, reachProfile: 'local' | 'national' | 'global') =>
      request<{ ok: boolean; reachProfile: string }>(
        'PATCH',
        `/domains/${domainId}/reach`,
        { reachProfile },
      ),
    listTracked: (ownedDomainId: string) =>
      request<import('./types').TrackedDomainSummary[]>('GET', `/domains/${ownedDomainId}/tracked`),
  },

  schemaDelivery: {
    set: (domainId: string, mode: import('./types').SchemaDeliveryMode) =>
      request<{ ok: boolean; schemaDeliveryMode: string }>(
        'PATCH',
        `/domains/${domainId}/schema-delivery`,
        { mode },
      ),
    seed: (domainId: string) =>
      request<{ ok: boolean; version: string }>('POST', `/domains/${domainId}/schema/seed`),
  },

  faqGenerations: {
    create: (domainId: string, body: { sourceUrl: string; suggestionId?: string }) =>
      request<import('./types').FaqGeneration>(
        'POST',
        `/domains/${domainId}/faq-generations`,
        body,
      ),
    listByDomain: (domainId: string, opts?: { status?: import('./types').FaqGenerationStatus }) => {
      const qs = new URLSearchParams();
      if (opts?.status) qs.set('status', opts.status);
      const query = qs.toString();
      return request<import('./types').FaqGeneration[]>(
        'GET',
        `/domains/${domainId}/faq-generations${query ? `?${query}` : ''}`,
      );
    },
    get: (id: string) =>
      request<import('./types').FaqGeneration>('GET', `/faq-generations/${id}`),
    updateQaPairs: (id: string, qaPairs: import('./types').FaqQaPair[]) =>
      request<import('./types').FaqGeneration>('PATCH', `/faq-generations/${id}`, { qaPairs }),
    approve: (id: string) =>
      request<import('./types').FaqGeneration>('POST', `/faq-generations/${id}/approve`),
    discard: (id: string) =>
      request<import('./types').FaqGeneration>('POST', `/faq-generations/${id}/discard`),
    getJsonLd: (id: string) =>
      request<{ jsonLd: string }>('GET', `/faq-generations/${id}/json-ld`),
  },

  backlinkTargets: {
    listByDomain: (domainId: string) =>
      request<{ targets: import('./types').BacklinkTarget[] }>(
        'GET',
        `/api/v1/domains/${domainId}/backlink-targets`,
      ),
    dismiss: (targetId: string) =>
      request<{ ok: boolean }>('PATCH', `/api/v1/backlink-targets/${targetId}/dismiss`),
  },

  outreachDrafts: {
    generate: (targetId: string, body?: { customAngleHint?: string }) =>
      request<import('./types').OutreachDraft>(
        'POST',
        `/api/v1/backlink-targets/${targetId}/drafts`,
        body ?? {},
      ),
    listByDomain: (domainId: string) =>
      request<{ drafts: import('./types').OutreachDraft[] }>(
        'GET',
        `/api/v1/domains/${domainId}/outreach-drafts`,
      ).then((r) => r.drafts),
    updateContent: (
      draftId: string,
      data: { draftSubject?: string; draftBody?: string },
    ) =>
      request<import('./types').OutreachDraft>(
        'PATCH',
        `/api/v1/outreach-drafts/${draftId}/content`,
        data,
      ),
    updateStatus: (draftId: string, toStatus: import('./types').OutreachDraftStatus) =>
      request<import('./types').OutreachDraft>(
        'POST',
        `/api/v1/outreach-drafts/${draftId}/status`,
        { toStatus },
      ),
  },

  onboarding: {
    getStatus: (domainId: string) =>
      request<import('./types').OnboardingStatus>('GET', `/domains/${domainId}/onboarding`),
    patchProgress: (
      domainId: string,
      partial: Partial<import('./types').OnboardingProgress>,
    ) =>
      request<{ progress: import('./types').OnboardingProgress; gatePasses: boolean; gateReasons: string[] }>(
        'PATCH',
        `/domains/${domainId}/onboarding/progress`,
        partial,
      ),
    setPrimaryGoal: (
      domainId: string,
      primaryGoal: import('./types').PrimaryGoal | null,
    ) =>
      request<{ primaryGoal: string | null; progress: import('./types').OnboardingProgress }>(
        'PATCH',
        `/domains/${domainId}/onboarding/primary-goal`,
        { primaryGoal },
      ),
    complete: (domainId: string) =>
      request<{ ok: boolean }>('POST', `/domains/${domainId}/onboarding/complete`),
    getBeaconMismatches: (domainId: string, limit?: number) => {
      const qs = limit !== undefined ? `?limit=${limit}` : '';
      return request<import('./types').BeaconMismatch[]>(
        'GET',
        `/domains/${domainId}/beacon-mismatches${qs}`,
      );
    },
  },

  // Keywords, trends, and content-briefs routers are mounted under /api/v1
  // (routes/v1/index.ts) — unlike the top-level /domains routes above.
  keywords: {
    list: (domainId: string) =>
      request<{ keywords: import('./types').KeywordVolumeEntry[] }>(
        'GET',
        `/api/v1/domains/${domainId}/keywords`,
      ),
    lookup: (domainId: string, keywords: string[], geography?: string) =>
      request<{ keywords: import('./types').KeywordVolumeEntry[] }>(
        'POST',
        `/api/v1/domains/${domainId}/keywords/lookup`,
        { keywords, ...(geography !== undefined ? { geography } : {}) },
      ),
  },

  trends: {
    getCategory: (domainId: string) =>
      request<import('./types').CategoryTrend>(
        'GET',
        `/api/v1/domains/${domainId}/trends/category`,
      ),
  },

  contentBriefs: {
    list: (domainId: string) =>
      request<{ briefs: import('./types').ContentBrief[] }>(
        'GET',
        `/api/v1/domains/${domainId}/content-briefs`,
      ),
    quota: (domainId: string) =>
      request<import('./types').ContentBriefQuota>(
        'GET',
        `/api/v1/domains/${domainId}/content-briefs/quota`,
      ),
    generate: (domainId: string, targetKeyword: string) =>
      request<import('./types').ContentBrief>(
        'POST',
        `/api/v1/domains/${domainId}/content-briefs`,
        { targetKeyword },
      ),
    get: (id: string) =>
      request<import('./types').ContentBrief>('GET', `/api/v1/content-briefs/${id}`),
    updateContent: (id: string, fields: Partial<Pick<import('./types').ContentBrief,
      'suggestedTitle' | 'metaDescription' | 'headings' | 'keywordVariants' | 'semanticTerms' | 'referenceUrls' | 'internalLinks'
    >>) =>
      request<import('./types').ContentBrief>('PATCH', `/api/v1/content-briefs/${id}/content`, fields),
    transition: (id: string, toStatus: import('./types').ContentBriefStatus, publishedUrl?: string) =>
      request<import('./types').ContentBrief>(
        'POST',
        `/api/v1/content-briefs/${id}/status`,
        { toStatus, ...(publishedUrl !== undefined ? { publishedUrl } : {}) },
      ),
    /** Download the Markdown export as a Blob — must NOT use request() since it calls res.json() */
    exportMarkdown: async (id: string): Promise<Blob> => {
      const headers: Record<string, string> = {};
      if (_token) headers['Authorization'] = `Bearer ${_token}`;
      const res = await fetch(`${API_BASE}/api/v1/content-briefs/${id}/export`, {
        method: 'POST',
        headers,
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      return res.blob();
    },
  },

  // Sprint 2.8b Step 6 — Progress tab
  progress: {
    audits: (domainId: string) =>
      request<{ audits: Array<{ id: string; startedAt: string; completedAt: string; score: number }> }>(
        'GET',
        `/api/v1/domains/${domainId}/progress/audits`,
      ),
    audit: (domainId: string, auditId: string) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      request<Record<string, any>>('GET', `/api/v1/domains/${domainId}/progress/audit/${auditId}`),
    window: (domainId: string, from: string, to: string) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      request<{ audits: Array<Record<string, any>> }>(
        'GET',
        `/api/v1/domains/${domainId}/progress/window?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    attributions: (domainId: string, from: string, to: string) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      request<{ attributions: Array<Record<string, any>> }>(
        'GET',
        `/api/v1/domains/${domainId}/progress/attributions?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    citationRate: (domainId: string, at: string) =>
      request<{ rate: number | null; source: string | null; auditId?: string; recordedAt?: string }>(
        'GET',
        `/api/v1/domains/${domainId}/progress/citation-rate?at=${encodeURIComponent(at)}`,
      ),
    shareOfModelVoice: (domainId: string, at: string) =>
      request<{ at: string; shareOfModelVoice: number | null; competitorMentions: unknown[]; auditId?: string }>(
        'GET',
        `/api/v1/domains/${domainId}/progress/share-of-model-voice?at=${encodeURIComponent(at)}`,
      ),
    headlineNarrative: (domainId: string, from: string, to: string) =>
      request<{ narrative: string; cached: boolean; fallback: boolean }>(
        'GET',
        `/api/v1/domains/${domainId}/progress/headline-narrative?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
  },

  // AFI-E1 — Customer dashboard
  cdn: {
    /**
     * Public URL of the domain's approved schema as a raw JSON-LD document.
     * This is the raw-HTML delivery path — visible to AI crawlers (ChatGPT,
     * Perplexity, Claude) that do not execute the client-side beacon tag.
     */
    schemaJsonldUrl: (domainId: string): string => `${API_BASE}/cdn/schema/${domainId}.jsonld`,
    /** Fetches the raw JSON-LD document text for the paste-able snippet. */
    schemaJsonld: async (domainId: string): Promise<string> => {
      const res = await fetch(`${API_BASE}/cdn/schema/${domainId}.jsonld`);
      if (!res.ok) throw new Error(`schema fetch failed: ${res.status}`);
      return res.text();
    },
    /**
     * Fetches Snippet 1 — the ready-to-paste <script type="application/ld+json"
     * data-fixmyseo-v="N">...</script> block for inline AI-crawler delivery.
     */
    snippetBlock: async (domainId: string): Promise<string> => {
      const res = await fetch(`${API_BASE}/cdn/snippet-block/${domainId}`);
      if (!res.ok) throw new Error(`snippet fetch failed: ${res.status}`);
      return res.text();
    },
    /** URL to download the CF Pages _middleware.ts file with domain ID baked in. */
    cfMiddlewareUrl: (domainId: string): string =>
      `${API_BASE}/cdn/cf-middleware/${domainId}.ts`,
  },

  afi: {
    pendingFixCounts: (domainId: string) =>
      request<{
        domainId: string;
        counts: { schema: number; 'meta-header': number; citation: number; content: number };
        total: number;
      }>('GET', `/api/v1/afi/e1/domains/${domainId}/pending-fixes`),

    listFixes: (domainId: string, bucket: 'schema' | 'meta-header' | 'citation' | 'content') =>
      request<{
        bucket: string;
        fixes: Array<{
          id: string;
          fixPayload: Record<string, unknown>;
          editedPayload: Record<string, unknown> | null;
          editedAt: string | null;
          methodologyRef: string;
          customerApprovedAt: string | null;
          createdAt: string;
          // schema-specific
          schemaType?: string;
          autoDeployEligible?: boolean;
          // meta-header-specific
          fixType?: string;
          fixCategory?: string;
          pillar?: string;
          // citation-specific
          platform?: string;
          actionType?: string;
        }>;
      }>('GET', `/api/v1/afi/e1/domains/${domainId}/fixes?bucket=${bucket}`),

    recentDeployments: (domainId: string, limit = 20) =>
      request<{
        domainId: string;
        events: Array<{
          id: string;
          fixBucket: string;
          fixRowId: string;
          deploymentSurface: string;
          dispatchStatus: 'dispatched' | 'failed' | 'retrying' | 'abandoned';
          dispatchAttempt: number;
          dispatchedAt: string;
          completedAt: string | null;
          failureReason: string | null;
        }>;
      }>('GET', `/api/v1/afi/e1/domains/${domainId}/recent-deployments?limit=${limit}`),

    approveFix: (bucket: string, fixId: string) =>
      request<{ ok: boolean; fixId: string; bucket: string; action: string }>(
        'POST',
        `/api/v1/afi/e1/fixes/${bucket}/${fixId}/approve`,
      ),

    rejectFix: (bucket: string, fixId: string) =>
      request<{ ok: boolean; fixId: string; bucket: string; action: string }>(
        'POST',
        `/api/v1/afi/e1/fixes/${bucket}/${fixId}/reject`,
      ),

    editFix: (bucket: string, fixId: string, payload: Record<string, unknown>) =>
      request<{ ok: boolean; fixId: string; bucket: string; action: string }>(
        'PATCH',
        `/api/v1/afi/e1/fixes/${bucket}/${fixId}`,
        { payload },
      ),
  },

  aiTruth: {
    getStats: (domainId: string) =>
      request<{
        totalDerived: number;
        totalApproved: number;
        coveragePct: number;
        groundingSitePct: number;
        questionAnticipationCount: number;
      }>('GET', `/api/v1/domains/${domainId}/ai-truth/stats`),

    getServingStats: (domainId: string) =>
      request<{
        totalQueries:     number;
        matchedQueries:   number;
        serveVolumePct:   number;
        namedCitations:   number;
        namedCitationPct: number;
        periodDays:       number;
      }>('GET', `/api/v1/domains/${domainId}/ai-truth/serving-stats`),

    listAnswers: (domainId: string) =>
      request<import('./types').AiTruthAnswer[]>('GET', `/api/v1/domains/${domainId}/ai-truth/answers`),

    approveAnswer: (domainId: string, answerId: string, answerText?: string) =>
      request<import('./types').AiTruthAnswer>(
        'POST',
        `/api/v1/domains/${domainId}/ai-truth/answers/${answerId}/approve`,
        answerText !== undefined ? { answerText } : undefined,
      ),

    rejectAnswer: (domainId: string, answerId: string) =>
      request<import('./types').AiTruthAnswer>('POST', `/api/v1/domains/${domainId}/ai-truth/answers/${answerId}/reject`),

    patchAnswer: (domainId: string, answerId: string, answerText: string) =>
      request<{ prior: import('./types').AiTruthAnswer; next: import('./types').AiTruthAnswer }>(
        'PATCH',
        `/api/v1/domains/${domainId}/ai-truth/answers/${answerId}`,
        { answerText },
      ),

    // C7 Stage 3 — onboarding wizard endpoints

    /** Zero-DNS default provisioning: provisions {slug}.customers.fixmyseo.io. Idempotent. */
    activate: (domainId: string) =>
      request<import('./types').AiTruthCustomHostname>('POST', `/api/v1/domains/${domainId}/ai-truth/activate`),

    /** Poll current custom hostname status (lazy-syncs ssl_status from CF). 404 = not provisioned. */
    getCustomHostname: (domainId: string) =>
      request<import('./types').AiTruthCustomHostname>('GET', `/api/v1/domains/${domainId}/ai-truth/custom-hostname`),

    /** Provision a customer-owned first-party hostname (e.g. ai.acme.com). */
    provisionCustomHostname: (domainId: string, hostname: string) =>
      request<import('./types').AiTruthCustomHostname>('POST', `/api/v1/domains/${domainId}/ai-truth/custom-hostname`, { hostname }),

    /** Re-drive DCV TXT writes for a stuck pending_validation hostname. */
    retryDcv: (domainId: string) =>
      request<import('./types').AiTruthCustomHostname>('POST', `/api/v1/domains/${domainId}/ai-truth/custom-hostname/retry-dcv`),

    /** Deprovision custom hostname, purge R2, delete DB row. */
    deleteCustomHostname: (domainId: string) =>
      request<void>('DELETE', `/api/v1/domains/${domainId}/ai-truth/custom-hostname`),

    // C8 — WordPress plugin presence

    /** Check if a WP plugin has registered presence for this domain. */
    getWpPluginPresence: (domainId: string) =>
      request<{ active: boolean; siteUrl?: string; registeredAt?: string; lastPingAt?: string | null }>(
        'GET',
        `/api/v1/domains/${domainId}/ai-truth/wp-plugin-presence`,
      ),

    // C9 — TruthSynq freshness + beacon visit-length

    /** Freshness: pending re-drafts, deleted-source count, decay, last sync. */
    getFreshness: (domainId: string) =>
      request<{
        pendingRedraftCount: number;
        deletedSourceCount:  number;
        lastSyncAt:          string | null;
        avgDecayDays:        number | null;
      }>('GET', `/api/v1/domains/${domainId}/ai-truth/freshness`),

    /** Visit Length: per-engine avg engagement seconds (10-session guard). */
    getVisitLength: (domainId: string) =>
      request<{
        windowDays:      number;
        minSessions:     number;
        engines:         Array<{ engine: string; sessions: number; avgEngagementSeconds: number }>;
        enginesBelow10:  string[];
      }>('GET', `/api/v1/domains/${domainId}/ai-truth/visit-length`),

    // C10 — Composite AI Accuracy Score

    /** Composite score with component decomposition + grace-period flag. */
    getScore: (domainId: string) =>
      request<{
        overallScore:  number | null;
        band:          'poor' | 'building' | 'good' | 'strong' | 'excellent' | null;
        gracePeriod:   boolean;
        components: {
          coverage:  { score: number; weight: number; source: string };
          grounding: { score: number; weight: number; source: string };
          serving:   { score: number; weight: number; source: string };
          reach:     { score: number; weight: number; source: string };
          freshness: { score: number; weight: number; source: string };
        };
        inputs: {
          totalApproved:             number;
          totalQueries:              number;
          questionAnticipationCount: number;
          lastSyncAt:                string | null;
        };
      }>('GET', `/api/v1/domains/${domainId}/ai-truth/score`),

    // C10.5 — Serving mode state machine

    /** Current serving mode + canonical endpoint + available resolution actions. */
    getServingMode: (domainId: string) =>
      request<{
        mode:                'saas' | 'plugin' | 'plugin-grace' | 'disconnected' | null;
        canonicalBase:       string | null;
        graceStartedAt:      string | null;
        graceCompletedAt:    string | null;
        consecutiveFailures: number;
        actions:             string[];
      }>('GET', `/api/v1/domains/${domainId}/ai-truth/serving-mode`),

    /** Reconnect plugin after a disconnect (customer confirms plugin is back). */
    reconnectPlugin: (domainId: string) =>
      request<{ mode: string; canonicalBase: string | null }>(
        'POST', `/api/v1/domains/${domainId}/ai-truth/serving-mode/reconnect`,
      ),

    /** Switch to SaaS serving after a disconnect (re-provision subdomain). */
    switchToSaas: (domainId: string) =>
      request<{ mode: string; hostname: string; sslStatus: string }>(
        'POST', `/api/v1/domains/${domainId}/ai-truth/serving-mode/switch-to-saas`,
      ),

    /** 30-day trend of daily score snapshots. */
    getScoreTrend: (domainId: string) =>
      request<{
        trend: Array<{
          date:         string;
          overallScore: number;
          band:         string;
          gracePeriod:  boolean;
        }>;
      }>('GET', `/api/v1/domains/${domainId}/ai-truth/score/trend`),
  },
};
